import { getLogger } from '@trimble/logging';

/** Initial per-tile / circuit backoff after the first systemic failure. */
export const TILE_FETCH_BASE_RETRY_MS = 5_000;

/** Cap for exponential backoff so recovery stays bounded. */
export const TILE_FETCH_MAX_RETRY_MS = 60_000;

/** Open the shared circuit after this many consecutive systemic failures. */
const CIRCUIT_OPEN_AFTER_FAILURES = 3;

/** Base of the exponential backoff (`base^n`). */
const BACKOFF_BASE = 2;

/** Max exponent used when computing backoff. */
const MAX_BACKOFF_EXPONENT = 4;

/** Minimum gap between rate-limited failure log emissions. */
const LOG_SUMMARY_INTERVAL_MS = 10_000;

const MS_PER_SECOND = 1000;

const SYSTEMIC_TILE_HTTP_STATUS = /:\s*(401|403|408|429|5\d\d)\b/;
const SYSTEMIC_TILE_STATUS_TEXT =
  /:\s*(unauthorized|forbidden|request timeout|too many requests|internal server error|bad gateway|service unavailable|gateway timeout)\b/i;

export type TileFetchFailureLogger = Pick<
  ReturnType<typeof getLogger>,
  'error' | 'warn' | 'debug'
>;

/**
 * Shared tile-fetch failure state for a FeaturesServiceModel tile tree.
 *
 * When the tile service is unreachable (CORS, network down, auth), every
 * visible tile would otherwise retry and log independently — flooding the
 * console and the network. This tracker:
 * 1. Opens a circuit after consecutive systemic failures so new fetches pause
 * 2. Rate-limits ERROR logs to one detailed entry plus periodic summaries
 * 3. Exposes exponential backoff delays for per-tile retry scheduling
 *
 * Owned by FeaturesServiceModel and injected into FeaturesServiceNode. This is
 * not a capability coordinator.
 */
export class TileFetchFailureTracker {
  private consecutiveSystemicFailures = 0;
  private circuitOpenUntilMs = 0;
  private suppressedFailureLogs = 0;
  private lastFailureLogAtMs = 0;
  private lastFailureMessage = '';
  private lastFailureTileId = '';
  private circuitOpenLogged = false;

  public isFetchAllowed(nowMs: number = Date.now()): boolean {
    return nowMs >= this.circuitOpenUntilMs;
  }

  public getRetryDelayMs(
    tileFailureAttempts: number,
    nowMs: number = Date.now()
  ): number {
    const safeAttempts = Math.max(1, tileFailureAttempts);
    const exponent = Math.min(safeAttempts - 1, MAX_BACKOFF_EXPONENT);
    const tileDelay = Math.min(
      TILE_FETCH_BASE_RETRY_MS * BACKOFF_BASE ** exponent,
      TILE_FETCH_MAX_RETRY_MS
    );
    const circuitRemainingMs = Math.max(0, this.circuitOpenUntilMs - nowMs);
    return Math.max(tileDelay, circuitRemainingMs);
  }

  public recordSuccess(): void {
    this.consecutiveSystemicFailures = 0;
    this.circuitOpenUntilMs = 0;
    this.circuitOpenLogged = false;
    this.flushSuppressedLogsIfNeeded();
  }

  public recordFailure(error: unknown, nowMs: number = Date.now()): void {
    if (!TileFetchFailureTracker.isSystemicFetchFailure(error)) {
      return;
    }

    this.consecutiveSystemicFailures += 1;

    if (this.consecutiveSystemicFailures < CIRCUIT_OPEN_AFTER_FAILURES) {
      return;
    }

    const openExponent = Math.min(
      this.consecutiveSystemicFailures - CIRCUIT_OPEN_AFTER_FAILURES,
      MAX_BACKOFF_EXPONENT
    );
    const openForMs = Math.min(
      TILE_FETCH_BASE_RETRY_MS * BACKOFF_BASE ** openExponent,
      TILE_FETCH_MAX_RETRY_MS
    );
    this.circuitOpenUntilMs = Math.max(
      this.circuitOpenUntilMs,
      nowMs + openForMs
    );
  }

  public logTileFailure(
    logger: TileFetchFailureLogger,
    tileId: string,
    error: unknown,
    nowMs: number = Date.now()
  ): void {
    const message =
      error instanceof Error ? error.message : String(error ?? 'unknown error');

    if (this.shouldEmitFailureLog(nowMs)) {
      this.flushSuppressedLogsIfNeeded(logger);
      logger.error(
        `[FeaturesServiceNode ${tileId}] Error during requestData:`,
        { tileId },
        error
      );
      this.lastFailureLogAtMs = nowMs;
      this.lastFailureMessage = message;
      this.lastFailureTileId = tileId;
      this.suppressedFailureLogs = 0;
    } else {
      this.suppressedFailureLogs += 1;
      this.lastFailureMessage = message;
      this.lastFailureTileId = tileId;
    }

    if (
      !this.isFetchAllowed(nowMs) &&
      !this.circuitOpenLogged &&
      this.consecutiveSystemicFailures >= CIRCUIT_OPEN_AFTER_FAILURES
    ) {
      const openForMs = Math.max(0, this.circuitOpenUntilMs - nowMs);
      logger.warn(
        `[FeaturesService] Tile fetch circuit open for ~${Math.round(openForMs / MS_PER_SECOND)}s after ${this.consecutiveSystemicFailures} consecutive systemic failures. Pausing new tile requests.`,
        {
          consecutiveFailures: this.consecutiveSystemicFailures,
          openForMs,
          lastTileId: tileId,
          lastError: message,
        }
      );
      this.circuitOpenLogged = true;
    }
  }

  public static isSystemicFetchFailure(error: unknown): boolean {
    if (TileFetchFailureTracker.isAbortError(error)) {
      return false;
    }

    if (!(error instanceof Error) && typeof error !== 'object') {
      return false;
    }

    const message =
      error instanceof Error
        ? error.message
        : String((error as { message?: unknown }).message ?? error);

    // Tile-fetcher HTTP errors: only treat auth/throttle/server failures as systemic.
    // (Must run before the generic "Failed to fetch" check — that phrase is also
    // used in fetcher error messages for 4xx responses.) Combined fetches may
    // include status text without a numeric code.
    if (/failed to fetch tile/i.test(message)) {
      return (
        SYSTEMIC_TILE_HTTP_STATUS.test(message) ||
        SYSTEMIC_TILE_STATUS_TEXT.test(message)
      );
    }

    if (/^failed to fetch$/i.test(message.trim())) {
      return true;
    }

    if (/networkerror|network request failed|load failed/i.test(message)) {
      return true;
    }

    return error instanceof TypeError;
  }

  private shouldEmitFailureLog(nowMs: number): boolean {
    if (this.lastFailureLogAtMs === 0) {
      return true;
    }
    return nowMs - this.lastFailureLogAtMs >= LOG_SUMMARY_INTERVAL_MS;
  }

  private flushSuppressedLogsIfNeeded(logger?: TileFetchFailureLogger): void {
    if (this.suppressedFailureLogs <= 0 || !logger) {
      return;
    }

    logger.warn(
      `[FeaturesService] Suppressed ${this.suppressedFailureLogs} similar tile fetch failure log(s). Last: tile ${this.lastFailureTileId} — ${this.lastFailureMessage}`,
      {
        suppressedCount: this.suppressedFailureLogs,
        lastTileId: this.lastFailureTileId,
        lastError: this.lastFailureMessage,
      }
    );
    this.suppressedFailureLogs = 0;
  }

  private static isAbortError(error: unknown): boolean {
    return (
      typeof error === 'object' &&
      error !== null &&
      'name' in error &&
      (error as { name: string }).name === 'AbortError'
    );
  }
}
